// src/pages/AdminProductsPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import Loader from "../layouts/components/Loader";
import toast from "react-hot-toast";
import { productVariantsAPI } from "../utils/api";
import "./AdminProductsPage.css";

const API_BASE = "https://api.mariamlyluxe.com/public/api";

export default function AdminProductsPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [subcategoriesLoading, setSubcategoriesLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Variants state
  const [showVariantsModal, setShowVariantsModal] = useState(false);
  const [selectedProductForVariants, setSelectedProductForVariants] = useState(null);
  const [productVariants, setProductVariants] = useState([]);
  const [showAddVariantModal, setShowAddVariantModal] = useState(false);
  const [newVariant, setNewVariant] = useState({ 
    color: '', 
    hex_color: '', 
    size: '', 
    sku: '', 
    quantity: '', 
    regular_price: '',
    discount: '',
    buying_price: '',
    weight: '',
    images: [] 
  });
  const [variantsLoading, setVariantsLoading] = useState(false);
  
  // Variant deletion confirmation state
  const [showDeleteVariantModal, setShowDeleteVariantModal] = useState(false);
  const [variantToDelete, setVariantToDelete] = useState(null);
  
  // Variant editing state
  const [showEditVariantModal, setShowEditVariantModal] = useState(false);
  const [editingVariant, setEditingVariant] = useState(null);
  const [editVariantForm, setEditVariantForm] = useState({
    color: '',
    hex_color: '',
    size: '',
    sku: '',
    quantity: '',
    regular_price: '',
    discount: '',
    buying_price: '',
    weight: '',
    sort_order: 0,
    images: []
  });
  const [isUpdatingVariant, setIsUpdatingVariant] = useState(false);
  
  // Search and pagination state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFilters, setSearchFilters] = useState({
    category_id: "",
    subcategory_id: "",
    brand_id: "",
    tags: "",
    sort: "latest",
    low_stock: false
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1
  });
  const [isSearching, setIsSearching] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    sku: "",
    desc: "",
    category_id: "",
    category_ids: [], // Multiple categories support
    subcategory_id: "",
    brand_id: "",
    buying_price: "",
    regular_price: "",
    quantity: "",
    discount: "",
    weight: "",
    ingredients: "",
    usage_instructions: "",
    tags: "",
    is_trending: false,
    is_new: false,
    hot_offer: false,
    new_until: "",
    image: null, // Change from images: [] to image: null
    // Variant fields
    variant_color: "",
    variant_hex_color: "",
    variant_size: "",
    variant_images: []
  });

  // Fetch subcategories when primary category changes
  useEffect(() => {
    const cid = formData.category_id;
    if (!cid) {
      setSubcategories([]);
      return;
    }
    const fetchSubcategoriesByCategory = async (categoryId) => {
      const token = getToken();
      if (!token) return;
      setSubcategoriesLoading(true);
      try {
        const headers = { Authorization: `Bearer ${token}`, Accept: "application/json" };
        const res = await fetch(`${API_BASE}/subcategories?category_id=${categoryId}`, { headers });
        if (res.ok) {
          const json = await res.json();
          const list = Array.isArray(json?.data) ? json.data : Array.isArray(json?.subcategories) ? json.subcategories : [];
          setSubcategories(list);
        } else {
          setSubcategories([]);
        }
      } catch (err) {
        // keep UI responsive even if network fails
        setSubcategories([]);
        console.error("Subcategories fetch error", err);
      } finally {
        setSubcategoriesLoading(false);
      }
    };
    fetchSubcategoriesByCategory(cid);
  }, [formData.category_id]);

  // ===== Helpers =====
  const getToken = () =>
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    "";

  const getFirstImageUrl = (product) => {
    // 💡 FIX: Check variants for the first image, as all images (including single ones) are now stored here.
    if (product.variants && Array.isArray(product.variants) && product.variants.length > 0) {
        // Loop through all variants (including the 'Default' one)
        for (const variant of product.variants) {
            if (variant.images && Array.isArray(variant.images) && variant.images.length > 0) {
                const firstImage = variant.images[0];
                
                // Prioritize 'url' if it exists (usually set by Laravel API Resource)
                if (firstImage?.url) return firstImage.url;
                
                // Fallback: Construct the URL from the 'path' field
                if (firstImage?.path) return `${API_BASE.replace("/api", "")}/storage/${firstImage.path}`;
            }
        }
    }
  
    return null; // No image found on any variant
  };

  // Helper function to get total quantity for a product (including variants)
  // Example: If product has variants with quantities [5, 10, 3], total = 5 + 10 + 3 = 18
  const getTotalQuantity = (product) => {
    if (product.variants && Array.isArray(product.variants) && product.variants.length > 0) {
      // Sum all variant quantities: variant1.quantity + variant2.quantity + ...
      const total = product.variants.reduce((total, variant) => {
        const variantQuantity = parseInt(variant.quantity) || 0;
        return total + variantQuantity;
      }, 0);
      
      // Debug logging (remove in production)
      if (process.env.NODE_ENV === 'development') {
        console.log(`Product ${product.name} variants:`, product.variants.map(v => ({ 
          color: v.color, 
          size: v.size, 
          quantity: v.quantity 
        })));
        console.log(`Total quantity: ${total}`);
      }
      
      return total;
    }
    // Fallback to product quantity if no variants
    return parseInt(product.quantity) || 0;
  };

  // Helper function to check if product has variants
  const hasVariants = (product) => {
    return product.variants && Array.isArray(product.variants) && product.variants.length > 0;
  };

  // Helper function to check if new variant data is being created
  const hasNewVariantData = () => {
    return (formData.variant_color || formData.variant_size) && formData.variant_images.length > 0;
  };

  // Helper function to check if product is low stock (threshold: 5 or less)
  const isLowStock = (product) => {
    const totalQuantity = getTotalQuantity(product);
    return totalQuantity <= 5 && totalQuantity > 0;
  };

  // Helper function to check if product is out of stock
  const isOutOfStock = (product) => {
    return getTotalQuantity(product) === 0;
  };

  // Test function for quantity calculation (can be called from browser console)
  const testQuantityCalculation = () => {
    console.log("=== Testing Quantity Calculation ===");
    products.forEach((product, index) => {
      if (hasVariants(product)) {
        console.log(`\nProduct ${index + 1}: ${product.name}`);
        console.log("Variants:", product.variants.map(v => ({
          color: v.color,
          size: v.size,
          quantity: v.quantity
        })));
        console.log("Total calculated:", getTotalQuantity(product));
        console.log("Manual sum:", product.variants.reduce((sum, v) => sum + (parseInt(v.quantity) || 0), 0));
      }
    });
  };

  // Make test function available globally for debugging
  if (process.env.NODE_ENV === 'development') {
    window.testQuantityCalculation = testQuantityCalculation;
  }
  useEffect(() => {
    const token = getToken();
    const userRole = localStorage.getItem("user_role");

    if (!token || userRole !== "1") {
      navigate("/");
      return;
    }

    fetchData(token);
  }, [navigate]);

  const searchProducts = async (token, query = "", filters = {}, page = 1) => {
    setIsSearching(true);
    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      };

      // Build search parameters
      const searchParams = new URLSearchParams();
      if (query.trim()) searchParams.append('q', query.trim());
      if (filters.category_id) searchParams.append('category_id', filters.category_id);
      if (filters.subcategory_id) searchParams.append('subcategory_id', filters.subcategory_id);
      if (filters.brand_id) searchParams.append('brand_id', filters.brand_id);
      if (filters.sort) searchParams.append('sort', filters.sort);
      if (filters.low_stock) searchParams.append('low_stock', '1');
      searchParams.append('page', page);
      searchParams.append('per_page', pagination.per_page);

      const searchUrl = `${API_BASE}/products/admin/search?${searchParams.toString()}`;
      const productsRes = await fetch(searchUrl, { headers });

      if (productsRes.ok) {
        const searchData = await productsRes.json();
        if (searchData.success) {
          setProducts(searchData.products.data || []);
          setPagination({
            current_page: searchData.products.current_page || 1,
            per_page: searchData.products.per_page || 20,
            total: searchData.products.total || 0,
            last_page: searchData.products.last_page || 1
          });
        } else {
          setProducts([]);
        }
      } else {
        setProducts([]);
      }
    } catch (error) {
      toast.error("Error searching products");
      console.error("Search error:", error);
      setProducts([]);
    } finally {
      setIsSearching(false);
    }
  };

  const refreshProductsList = (token) => {
    const hasActiveSearch =
      (searchQuery && searchQuery.trim() !== "") ||
      searchFilters.category_id ||
      searchFilters.subcategory_id ||
      searchFilters.brand_id ||
      searchFilters.tags ||
      searchFilters.sort !== "latest" ||
      searchFilters.low_stock;

    if (searchFilters.low_stock) {
      fetchLowStockProducts(token);
    } else if (hasActiveSearch) {
      // Keep using current search text/filters
      searchProducts(token, searchQuery, searchFilters, pagination.current_page || 1);
    } else {
      // No search/filters -> full list
      fetchData(token);
    }
  };

  const fetchData = async (token) => {
    setLoading(true);
    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      };

      const [productsRes, categoriesRes, subcategoriesRes, brandsRes] = await Promise.all([
        fetch(`${API_BASE}/products/admin`, { headers }),
        fetch(`${API_BASE}/categories`, { headers }),
        fetch(`${API_BASE}/subcategories`, { headers }),
        fetch(`${API_BASE}/brands`, { headers }),
      ]);

      if (productsRes.ok) {
        const productsData = await productsRes.json();
        // Handle the response structure: {success: true, products: {data: [...], current_page: 1, ...}}
        if (productsData.success && productsData.products) {
          const list = Array.isArray(productsData.products.data)
            ? productsData.products.data
            : [];
          setProducts(list);
          // Set pagination for initial load
          setPagination({
            current_page: productsData.products.current_page || 1,
            per_page: productsData.products.per_page || 20,
            total: productsData.products.total || 0,
            last_page: productsData.products.last_page || 1
          });
        } else {
          // Fallback for different response structure
          const list = Array.isArray(productsData?.data)
            ? productsData.data
            : Array.isArray(productsData)
            ? productsData
            : [];
          setProducts(list);
          setPagination({
            current_page: productsData.current_page || 1,
            per_page: productsData.per_page || 20,
            total: productsData.total || 0,
            last_page: productsData.last_page || 1
          });
        }
      } else {
        setProducts([]);
      }

      if (categoriesRes.ok) {
        const categoriesData = await categoriesRes.json();
        if (Array.isArray(categoriesData?.categories)) {
          setCategories(categoriesData.categories);
        } else if (Array.isArray(categoriesData?.data)) {
          setCategories(categoriesData.data);
        } else if (Array.isArray(categoriesData)) {
          setCategories(categoriesData);
        } else {
          setCategories([]);
        }
      } else {
        setCategories([]);
      }

      if (subcategoriesRes.ok) {
        const subcategoriesData = await subcategoriesRes.json();
        if (subcategoriesData.success && Array.isArray(subcategoriesData?.data)) {
          setSubcategories(subcategoriesData.data);
        } else if (Array.isArray(subcategoriesData?.subcategories)) {
          setSubcategories(subcategoriesData.subcategories);
        } else if (Array.isArray(subcategoriesData?.data)) {
          setSubcategories(subcategoriesData.data);
        } else if (Array.isArray(subcategoriesData)) {
          setSubcategories(subcategoriesData);
        } else {
          setSubcategories([]);
        }
      } else {
        setSubcategories([]);
      }

      if (brandsRes.ok) {
        const brandsData = await brandsRes.json();
        if (Array.isArray(brandsData?.brands)) {
          setBrands(brandsData.brands);
        } else if (Array.isArray(brandsData?.data)) {
          setBrands(brandsData.data);
        } else if (Array.isArray(brandsData)) {
          setBrands(brandsData);
        } else {
          setBrands([]);
        }
      } else {
        setBrands([]);
      }
    } catch (error) {
      toast.error("Error fetching data");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]:
          type === "checkbox" ? checked :
          type === "file" ? files[0] : // Take only the first file
          value,
      };
      
      // Reset subcategory when category changes
      if (name === "category_id") {
        newData.subcategory_id = "";
      }
      
      return newData;
    });
  };

  const handleCategoryChange = (categoryId) => {
    setFormData((prev) => {
      const currentIds = prev.category_ids || [];
      const isSelected = currentIds.includes(categoryId);
      
      if (isSelected) {
        // Remove category
        return {
          ...prev,
          category_ids: currentIds.filter(id => id !== categoryId)
        };
      } else {
        // Add category
        return {
          ...prev,
          category_ids: [...currentIds, categoryId]
        };
      }
    });
  };

  // Search handlers
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Debounce search
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      const token = getToken();
      if (token) {
        searchProducts(token, value, searchFilters, 1);
      }
    }, 500);
  };

  const handleFilterChange = (filterName, value) => {
    const newFilters = { ...searchFilters, [filterName]: value };
    setSearchFilters(newFilters);
    
    const token = getToken();
    if (token) {
      if (filterName === 'low_stock' && value) {
        // If low stock filter is enabled, fetch low stock products
        fetchLowStockProducts(token);
      } else if (filterName === 'low_stock' && !value) {
        // If low stock filter is disabled, go back to normal search
        searchProducts(token, searchQuery, newFilters, 1);
      } else {
        // For other filters, use normal search
        searchProducts(token, searchQuery, newFilters, 1);
      }
    }
  };

  const handlePageChange = (page) => {
    const token = getToken();
    if (token) {
      if (searchFilters.low_stock) {
        // Handle pagination for low stock products
        fetchLowStockProductsPage(token, page);
      } else {
        searchProducts(token, searchQuery, searchFilters, page);
      }
    }
  };

  // Function to fetch low stock products with pagination
  const fetchLowStockProductsPage = async (token, page = 1) => {
    setIsSearching(true);
    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      };

      const response = await fetch(`${API_BASE}/products/low-stock?page=${page}`, { headers });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.low_stock_products) {
          setProducts(data.low_stock_products.data || []);
          setPagination({
            current_page: data.low_stock_products.current_page || 1,
            per_page: data.low_stock_products.per_page || 20,
            total: data.low_stock_products.total || 0,
            last_page: data.low_stock_products.last_page || 1
          });
        } else {
          setProducts([]);
        }
      } else {
        setProducts([]);
        toast.error("Failed to fetch low stock products");
      }
    } catch (error) {
      toast.error("Error fetching low stock products");
      console.error("Low stock error:", error);
      setProducts([]);
    } finally {
      setIsSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchFilters({
      category_id: "",
      subcategory_id: "",
      brand_id: "",
      tags: "",
      sort: "latest",
      low_stock: false
    });
    const token = getToken();
    if (token) {
      fetchData(token);
    }
  };

  // Dedicated function to fetch low stock products
  const fetchLowStockProducts = async (token) => {
    setIsSearching(true);
    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      };

      const response = await fetch(`${API_BASE}/products/low-stock`, { headers });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.low_stock_products) {
          setProducts(data.low_stock_products.data || []);
          setPagination({
            current_page: data.low_stock_products.current_page || 1,
            per_page: data.low_stock_products.per_page || 20,
            total: data.low_stock_products.total || 0,
            last_page: data.low_stock_products.last_page || 1
          });
        } else {
          setProducts([]);
        }
      } else {
        setProducts([]);
        toast.error("Failed to fetch low stock products");
      }
    } catch (error) {
      toast.error("Error fetching low stock products");
      console.error("Low stock error:", error);
      setProducts([]);
    } finally {
      setIsSearching(false);
    }
  };

  // src/pages/AdminProductsPage.jsx

// ... (Around line 214)

const handleSubmit = async (e) => {
  e.preventDefault();
  const token = getToken();
  setIsSubmitting(true);

  // 1) Validate required fields
  const requiredFields = [
      "name",
      "category_id",
      "brand_id",
  ];

  // All price and quantity fields are now optional
  const missing = requiredFields.filter((f) => {
      const v = formData[f];
      return v === undefined || v === null || String(v).trim() === "";
  });

  if (missing.length) {
      toast.error(`Please fill all required fields: ${missing.join(", ")}`);
      setIsSubmitting(false); // Make sure to stop submission on failure
      return;
  }

  // 2) Build FormData AFTER validation
  const fd = new FormData();
  requiredFields.forEach((f) => fd.append(f, formData[f]));

  // Optional
  if (formData.sku) fd.append("sku", formData.sku);
  if (formData.desc) fd.append("desc", formData.desc);
  if (formData.subcategory_id) fd.append("subcategory_id", formData.subcategory_id);
  if (formData.quantity !== "" && formData.quantity !== null && formData.quantity !== undefined) {
      fd.append("quantity", formData.quantity);
  }
  if (formData.buying_price !== "" && formData.buying_price !== null && formData.buying_price !== undefined) {
      fd.append("buying_price", formData.buying_price);
  }
  if (formData.regular_price !== "" && formData.regular_price !== null && formData.regular_price !== undefined) {
      fd.append("regular_price", formData.regular_price);
  }
  if (formData.discount !== "" && formData.discount !== null && formData.discount !== undefined) {
      fd.append("discount", formData.discount);
  }
  if (formData.weight) fd.append("weight", formData.weight);
  if (formData.ingredients) fd.append("ingredients", formData.ingredients);
  if (formData.usage_instructions) fd.append("usage_instructions", formData.usage_instructions);
  if (formData.tags) fd.append("tags", formData.tags);
  if (formData.new_until) fd.append("new_until", formData.new_until);

  // Multiple categories - include primary category in the array
  const allCategoryIds = [...(formData.category_ids || [])];
  
  // Add primary category if it's not already in the array
  if (formData.category_id && !allCategoryIds.includes(formData.category_id)) {
    allCategoryIds.unshift(formData.category_id); // Add primary category first
  }
  
  if (allCategoryIds.length > 0) {
    allCategoryIds.forEach((categoryId, index) => {
      fd.append(`category_ids[${index}]`, categoryId);
    });
  }

  // Booleans (send "1"/"0")
  fd.append("is_trending", formData.is_trending ? "1" : "0");
  fd.append("is_new", formData.is_new ? "1" : "0");
  fd.append("hot_offer", formData.hot_offer ? "1" : "0");

  // 💡 FIX 1: Send single main image file to the Laravel controller
  if (formData.image instanceof File) {
      fd.append("image", formData.image);
  }

  // 3) Decide URL & spoof method when editing
  const isEdit = Boolean(editingProduct);
  
  if (!isEdit) {
    // Check if user is creating variants at product creation
    const hasNewVariantDataValue = hasNewVariantData();
    
    if (hasNewVariantDataValue) {
      // Send variant details nested for initial creation
      if (formData.variant_color) fd.append("variants[0][color]", formData.variant_color);
      if (formData.variant_hex_color) fd.append("variants[0][hex_color]", formData.variant_hex_color);
      if (formData.variant_size) fd.append("variants[0][size]", formData.variant_size);
      
      // Append variant images
      Array.from(formData.variant_images).forEach((file, index) => {
          fd.append(`variants[0][images][${index}]`, file);
      });
    } else if (formData.quantity) {
      // If quantity is provided, create a default variant
      // Backend will handle this automatically
    }
    // If no variants and no quantity, product will be created without stock
  }
  const url = isEdit
      ? `${API_BASE}/products/${editingProduct.id}`
      : `${API_BASE}/products`;

  if (isEdit) {
      fd.append("_method", "PUT"); // spoof PUT
  }

  try {
      const response = await fetch(url, {
          method: "POST", // always POST (PUT is spoofed above)
          headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
              // DO NOT set Content-Type for FormData
          },
          body: fd,
      });

      if (response.ok) {
          toast.success(isEdit ? "Product updated successfully" : "Product created successfully");
          setShowModal(false);
          setEditingProduct(null);
          resetForm();
          refreshProductsList(token);
      } else {
          const errorData = await response.json().catch(() => ({}));
          // show backend validation details if present
          if (errorData?.errors && typeof errorData.errors === "object") {
              const firstKey = Object.keys(errorData.errors)[0];
              const firstMsg = errorData.errors[firstKey]?.[0];
              toast.error(firstMsg || errorData.message || "Failed to save product");
          } else {
              toast.error(errorData.message || "Failed to save product");
          }
          console.error("Save error detail:", errorData);
      }
  } catch (error) {
      toast.error("Error saving product");
      console.error("Error:", error);
  } finally {
      setIsSubmitting(false);
  }
};

// ... (Rest of the component code)

  const handleEdit = (product) => {
    setEditingProduct(product);
    
    // Get all category IDs from the product's categories
    const allCategoryIds = product.categories ? product.categories.map(cat => cat.id) : [];
    
    // Set the first category as primary, or use the existing category_id if available
    const primaryCategoryId = product.category_id || (allCategoryIds.length > 0 ? allCategoryIds[0] : "");
    
    // Remove the primary category from the additional categories array
    const additionalCategoryIds = allCategoryIds.filter(id => id !== primaryCategoryId);
    
    setFormData({
      name: product.name ?? "",
      sku: product.sku ?? "",
      desc: product.desc ?? "",
      category_id: primaryCategoryId,
      category_ids: additionalCategoryIds,
      subcategory_id: product.subcategory_id ?? "",
      brand_id: product.brand_id ?? "",
      buying_price: product.buying_price ?? "",
      regular_price: product.regular_price ?? "",
      quantity: product.quantity ?? "",
      discount: product.discount ?? "",
      weight: product.weight ?? "",
      ingredients: product.ingredients ?? "",
      usage_instructions: product.usage_instructions ?? "",
      tags: product.tags ?? "",
      is_trending: Boolean(product.is_trending),
      is_new: Boolean(product.is_new),
      hot_offer: Boolean(product.hot_offer),
      new_until: product.new_until ? String(product.new_until).slice(0, 10) : "",
      image: null, // Change from images: [] to image: null
      // Variant fields
      variant_color: "",
      variant_hex_color: "",
      variant_size: "",
      variant_images: []
    });
    setShowModal(true);
  };

  const handleDelete = async (productId) => {
    const product = products.find(p => p.id === productId);
    setDeletingProduct(product);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingProduct) return;
    
    setIsDeleting(true);
    const token = getToken();

    try {
      const res = await fetch(`${API_BASE}/products/${deletingProduct.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (res.ok) {
        toast.success("Product deleted successfully");
        setShowDeleteModal(false);
        setDeletingProduct(null);
        refreshProductsList(token);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.message || "Failed to delete product");
      }
    } catch (error) {
      toast.error("Error deleting product");
      console.error("Error:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingProduct(null);
    setIsDeleting(false);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      sku: "",
      desc: "",
      category_id: "",
      category_ids: [],
      subcategory_id: "",
      brand_id: "",
      buying_price: "",
      regular_price: "",
      quantity: "",
      discount: "",
      weight: "",
      ingredients: "",
      usage_instructions: "",
      tags: "",
      is_trending: false,
      is_new: false,
      hot_offer: false,
      new_until: "",
      image: null, // Change from images: [] to image: null
      // Variant fields
      variant_color: "",
      variant_hex_color: "",
      variant_size: "",
      variant_images: []
    });
  };

  const openModal = () => {
    setEditingProduct(null);
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProduct(null);
    resetForm();
  };

  const getCategoryName = (categoryId) => {
    if (!Array.isArray(categories)) return "Unknown";
    const category = categories.find((cat) => Number(cat.id) === Number(categoryId));
    return category ? category.name : "Unknown";
  };

  const getProductCategories = (product) => {
    // If product has categories array, use that
    if (product.categories && Array.isArray(product.categories) && product.categories.length > 0) {
      return product.categories.map(cat => cat.name).join(", ");
    }
    
    // Fallback to category_id if categories array is not available
    if (product.category_id) {
      return getCategoryName(product.category_id);
    }
    
    return "No Category";
  };

  const getBrandName = (brandId) => {
    if (!Array.isArray(brands)) return "Unknown";
    const brand = brands.find((b) => Number(b.id) === Number(brandId));
    return brand ? brand.name : "Unknown";
  };

  const getSubcategoryName = (subcategoryId) => {
    if (!Array.isArray(subcategories) || !subcategoryId) return "None";
    const subcategory = subcategories.find((s) => Number(s.id) === Number(subcategoryId));
    return subcategory ? subcategory.name : "None";
  };

  // Variants functions
  const loadProductVariants = async (productId) => {
    try {
      setVariantsLoading(true);
      const data = await productVariantsAPI.getProduct(productId);
      setProductVariants(data.product.variants || []);
    } catch (err) {
      console.error('Error loading product variants:', err);
      toast.error('Failed to load product variants');
      setProductVariants([]);
    } finally {
      setVariantsLoading(false);
    }
  };

  const handleViewVariants = async (product) => {
    setSelectedProductForVariants(product);
    setShowVariantsModal(true);
    await loadProductVariants(product.id);
  };

  const handleAddVariant = async () => {
    if (!newVariant.color || newVariant.images.length === 0) {
      toast.error('Please provide color and at least one image');
      return;
    }

    // Validate hex color format if color is provided
    if (newVariant.color && newVariant.hex_color && !/^#[0-9A-Fa-f]{6}$/.test(newVariant.hex_color)) {
      toast.error('Please enter a valid hex color code (e.g., #FF69B4)');
      return;
    }

    // Validate quantity if provided
    if (newVariant.quantity && (isNaN(newVariant.quantity) || newVariant.quantity < 0)) {
      toast.error('Please enter a valid quantity (0 or greater)');
      return;
    }

    try {
      setVariantsLoading(true);
      const formData = new FormData();
      
      formData.append('color', newVariant.color);
      if (newVariant.hex_color) formData.append('hex_color', newVariant.hex_color);
      if (newVariant.size) formData.append('size', newVariant.size);
      if (newVariant.sku) formData.append('sku', newVariant.sku);
      if (newVariant.quantity) formData.append('quantity', newVariant.quantity);
      if (newVariant.regular_price) formData.append('regular_price', newVariant.regular_price);
      if (newVariant.discount) formData.append('discount', newVariant.discount);
      if (newVariant.buying_price) formData.append('buying_price', newVariant.buying_price);
      if (newVariant.weight) formData.append('weight', newVariant.weight);
      newVariant.images.forEach((image, index) => {
        formData.append(`images[${index}]`, image);
      });

      await productVariantsAPI.addVariant(selectedProductForVariants.id, formData);

      toast.success('Variant added successfully');
      setShowAddVariantModal(false);
      setNewVariant({ 
        color: '', 
        hex_color: '', 
        size: '', 
        sku: '', 
        quantity: '', 
        regular_price: '',
        discount: '',
        buying_price: '',
        weight: '',
        images: [] 
      });
      await loadProductVariants(selectedProductForVariants.id);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setVariantsLoading(false);
    }
  };

  const handleDeleteVariant = async (variantId) => {
    const variant = productVariants.find(v => v.id === variantId);
    setVariantToDelete(variant);
    setShowDeleteVariantModal(true);
  };

  const confirmDeleteVariant = async () => {
    if (!variantToDelete) return;
    
    try {
      setVariantsLoading(true);
      await productVariantsAPI.deleteVariant(variantToDelete.id);
      toast.success('Variant deleted successfully');
      setShowDeleteVariantModal(false);
      setVariantToDelete(null);
      await loadProductVariants(selectedProductForVariants.id);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setVariantsLoading(false);
    }
  };

  const cancelDeleteVariant = () => {
    setShowDeleteVariantModal(false);
    setVariantToDelete(null);
  };

  const handleDeleteImage = async (variantId, imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      setVariantsLoading(true);
      await productVariantsAPI.deleteImage(variantId, imageId);
      toast.success('Image deleted successfully');
      await loadProductVariants(selectedProductForVariants.id);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setVariantsLoading(false);
    }
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    setNewVariant(prev => ({
      ...prev,
      images: [...prev.images, ...files]
    }));
  };

  // Variant editing functions
  const handleEditVariant = (variant) => {
    setEditingVariant(variant);
    setEditVariantForm({
      color: variant.color || '',
      hex_color: variant.hex_color || '',
      size: variant.size || '',
      sku: variant.sku || '',
      quantity: variant.quantity || '',
      regular_price: variant.regular_price || '',
      discount: variant.discount || '',
      buying_price: variant.buying_price || '',
      weight: variant.weight || '',
      sort_order: variant.sort_order || 0,
      images: []
    });
    setShowEditVariantModal(true);
  };

  const handleUpdateVariant = async () => {
    if (!editingVariant || !editVariantForm.color) {
      toast.error('Please provide color');
      return;
    }

    // Validate hex color format if color is provided
    if (editVariantForm.color && editVariantForm.hex_color && !/^#[0-9A-Fa-f]{6}$/.test(editVariantForm.hex_color)) {
      toast.error('Please enter a valid hex color code (e.g., #FF69B4)');
      return;
    }

    // Validate quantity if provided
    if (editVariantForm.quantity && (isNaN(editVariantForm.quantity) || editVariantForm.quantity < 0)) {
      toast.error('Please enter a valid quantity (0 or greater)');
      return;
    }

    try {
      setIsUpdatingVariant(true);
      const token = getToken();
      const formData = new FormData();
      
      formData.append('color', editVariantForm.color);
      if (editVariantForm.hex_color) formData.append('hex_color', editVariantForm.hex_color);
      if (editVariantForm.size) formData.append('size', editVariantForm.size);
      if (editVariantForm.sku) formData.append('sku', editVariantForm.sku);
      if (editVariantForm.quantity) formData.append('quantity', editVariantForm.quantity);
      if (editVariantForm.regular_price) formData.append('regular_price', editVariantForm.regular_price);
      if (editVariantForm.discount) formData.append('discount', editVariantForm.discount);
      if (editVariantForm.buying_price) formData.append('buying_price', editVariantForm.buying_price);
      if (editVariantForm.weight) formData.append('weight', editVariantForm.weight);
      formData.append('sort_order', editVariantForm.sort_order);
      formData.append('_method', 'PUT'); // Spoof PUT method
      
      // Add new images if any
      editVariantForm.images.forEach((image, index) => {
        formData.append(`images[${index}]`, image);
      });

      const response = await fetch(`${API_BASE}/variants/${editingVariant.id}`, {
        method: 'POST', // Always POST with _method=PUT
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: formData
      });

      if (response.ok) {
        toast.success('Variant updated successfully');
        setShowEditVariantModal(false);
        setEditingVariant(null);
        setEditVariantForm({ 
          color: '', 
          hex_color: '', 
          size: '', 
          sku: '', 
          quantity: '', 
          regular_price: '',
          discount: '',
          buying_price: '',
          weight: '',
          sort_order: 0, 
          images: [] 
        });
        await loadProductVariants(selectedProductForVariants.id);
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Failed to update variant');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to update variant');
    } finally {
      setIsUpdatingVariant(false);
    }
  };

  const handleMoveVariantUp = async (variantId) => {
    try {
      setVariantsLoading(true);
      const token = getToken();
      
      // Find current variant and its position
      const currentIndex = productVariants.findIndex(v => v.id === variantId);
      if (currentIndex <= 0) {
        toast.error('Variant is already at the top');
        return;
      }
      
      // Create new order by swapping with the variant above
      const newOrder = productVariants.map((variant, index) => {
        if (index === currentIndex) {
          // Current variant gets the position of the one above
          return { id: variant.id, sort_order: productVariants[currentIndex - 1].sort_order };
        } else if (index === currentIndex - 1) {
          // The variant above gets the current variant's position
          return { id: variant.id, sort_order: productVariants[currentIndex].sort_order };
        }
        return { id: variant.id, sort_order: variant.sort_order };
      });

      const response = await fetch(`${API_BASE}/products/${selectedProductForVariants.id}/variants/reorder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          variants: newOrder
        })
      });

      if (response.ok) {
        toast.success('Variant moved up successfully');
        await loadProductVariants(selectedProductForVariants.id);
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Failed to move variant up');
      }
    } catch (err) {
      toast.error('Error moving variant up');
    } finally {
      setVariantsLoading(false);
    }
  };

  const handleMoveVariantDown = async (variantId) => {
    try {
      setVariantsLoading(true);
      const token = getToken();
      
      // Find current variant and its position
      const currentIndex = productVariants.findIndex(v => v.id === variantId);
      if (currentIndex >= productVariants.length - 1) {
        toast.error('Variant is already at the bottom');
        return;
      }
      
      // Create new order by swapping with the variant below
      const newOrder = productVariants.map((variant, index) => {
        if (index === currentIndex) {
          // Current variant gets the position of the one below
          return { id: variant.id, sort_order: productVariants[currentIndex + 1].sort_order };
        } else if (index === currentIndex + 1) {
          // The variant below gets the current variant's position
          return { id: variant.id, sort_order: productVariants[currentIndex].sort_order };
        }
        return { id: variant.id, sort_order: variant.sort_order };
      });

      const response = await fetch(`${API_BASE}/products/${selectedProductForVariants.id}/variants/reorder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          variants: newOrder
        })
      });

      if (response.ok) {
        toast.success('Variant moved down successfully');
        await loadProductVariants(selectedProductForVariants.id);
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Failed to move variant down');
      }
    } catch (err) {
      toast.error('Error moving variant down');
    } finally {
      setVariantsLoading(false);
    }
  };

  const handleReorderVariants = async (newOrder) => {
    try {
      setVariantsLoading(true);
      const token = getToken();
      const response = await fetch(`${API_BASE}/products/${selectedProductForVariants.id}/variants/reorder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          variants: newOrder
        })
      });

      if (response.ok) {
        toast.success('Variants reordered successfully');
        await loadProductVariants(selectedProductForVariants.id);
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Failed to reorder variants');
      }
    } catch (err) {
      toast.error('Error reordering variants');
    } finally {
      setVariantsLoading(false);
    }
  };

  const handleEditVariantImageUpload = (event) => {
    const files = Array.from(event.target.files);
    setEditVariantForm(prev => ({
      ...prev,
      images: [...prev.images, ...files]
    }));
  };

  const closeVariantsModal = () => {
    setShowVariantsModal(false);
    setSelectedProductForVariants(null);
    setProductVariants([]);
    setShowAddVariantModal(false);
    setNewVariant({ 
      color: '', 
      hex_color: '', 
      size: '', 
      sku: '', 
      quantity: '', 
      regular_price: '',
      discount: '',
      buying_price: '',
      weight: '',
      images: [] 
    });
    setShowDeleteVariantModal(false);
    setVariantToDelete(null);
    setShowEditVariantModal(false);
    setEditingVariant(null);
    setEditVariantForm({ 
      color: '', 
      hex_color: '', 
      size: '', 
      sku: '', 
      quantity: '', 
      regular_price: '',
      discount: '',
      buying_price: '',
      weight: '',
      sort_order: 0, 
      images: [] 
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-products">
          <div className="loading">Loading products...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-products">
        {/* Header */}
        <div className="admin-header">
          <div className="admin-title">
            <h1>Products Management</h1>
            <p>Manage your product inventory</p>
          </div>
          <div className="admin-actions">
            
            <button className="btn-primary" onClick={openModal}>
              <svg viewBox="0 0 24 24">
                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" />
              </svg>
              Add New Product
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="search-filters-container">
          <div className="search-section">
            <div className="search-input-group">
              <div className="search-icon">
                <svg viewBox="0 0 24 24">
                  <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" stroke="currentColor" strokeWidth="2" fill="none" />
                </svg>
              </div>
              <input
                type="text"
                placeholder={searchFilters.low_stock ? "Low Stock Products (search disabled)" : "Search products by name..."}
                value={searchQuery}
                onChange={handleSearchInputChange}
                className="search-input"
                disabled={searchFilters.low_stock}
              />
              {(searchQuery || searchFilters.low_stock) && (
                <button className="clear-search" onClick={clearSearch}>
                  <svg viewBox="0 0 24 24">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </button>
              )}
            </div>
            
            {searchFilters.low_stock && (
              <div className="active-filter-indicator">
                <span className="filter-badge low-stock-active">
                  <svg viewBox="0 0 24 24" width="14" height="14">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="currentColor"/>
                  </svg>
                  Low Stock Filter Active
                </span>
              </div>
            )}
            
            <div className="filters-row">
              <div className="filter-group">
                <label>Category</label>
                <select
                  value={searchFilters.category_id}
                  onChange={(e) => handleFilterChange('category_id', e.target.value)}
                >
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Temporarily disabled until subcategories table is created */}
              {/* <div className="filter-group">
                <label>Subcategory</label>
                <select
                  value={searchFilters.subcategory_id}
                  onChange={(e) => handleFilterChange('subcategory_id', e.target.value)}
                >
                  <option value="">All Subcategories</option>
                  {subcategories
                    .filter(sub => !searchFilters.category_id || sub.category_id == searchFilters.category_id)
                    .map((subcategory) => (
                    <option key={subcategory.id} value={subcategory.id}>
                      {subcategory.name}
                    </option>
                  ))}
                </select>
              </div> */}
              
              <div className="filter-group">
                <label>Brand</label>
                <select
                  value={searchFilters.brand_id}
                  onChange={(e) => handleFilterChange('brand_id', e.target.value)}
                >
                  <option value="">All Brands</option>
                  {brands.map((brand) => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="filter-group">
                <label>Tags</label>
                <input
                  type="text"
                  value={searchFilters.tags}
                  onChange={(e) => handleFilterChange('tags', e.target.value)}
                  placeholder="Search by tags..."
                  className="filter-input"
                />
              </div>
              
              <div className="filter-group">
                <label>Sort By</label>
                <select
                  value={searchFilters.sort}
                  onChange={(e) => handleFilterChange('sort', e.target.value)}
                >
                  <option value="latest">Latest</option>
                  <option value="name_asc">Name A-Z</option>
                  <option value="name_desc">Name Z-A</option>
                  <option value="price_low">Price Low to High</option>
                  <option value="price_high">Price High to Low</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={searchFilters.low_stock}
                    onChange={(e) => handleFilterChange('low_stock', e.target.checked)}
                  />
                  <span>Low Stock Only</span>
                </label>
              </div>
            </div>
          </div>
          
          {isSearching && (
            <div className="search-loading">
              <Loader size={20} strokeWidth={2} />
              <span>Searching...</span>
            </div>
          )}
        </div>

        {/* Products Table */}
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>Category</th>
                <th>Subcategory</th>
                <th>Brand</th>
                <th>Weight</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Tags</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr>
                  <td colSpan="11" className="empty-state">
                    No products found
                  </td>
                </tr>
              ) : (
                products.map((product) => {
                  const imgUrl = getFirstImageUrl(product);
                  return (
                    <tr key={product.id}>
                      <td>
                        <div className="product-imageeeeee">
                          {imgUrl ? (
                            <img
                              src={imgUrl}
                              alt={product.name}
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                                if (e.currentTarget.nextSibling) {
                                  e.currentTarget.nextSibling.style.display = "flex";
                                }
                              }}
                            />
                          ) : null}
                          <div
                            className="image-placeholder"
                            style={{ display: imgUrl ? "none" : "flex" }}
                          >
                            <svg viewBox="0 0 24 24">
                              <path
                                d="M4 16l4.586-4.586a2 2 0 0 1 2.828 0L16 16m-2-2l1.586-1.586a2 2 0 0 1 2.828 0L20 14m-6-6h.01M6 20h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                            </svg>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="product-name">
                          <strong>{product.name}</strong>
                          <small>{product.desc?.substring(0, 50)}...</small>
                          {product.ingredients && (
                            <div className="product-details">
                              <span className="detail-label">Ingredients:</span>
                              <span className="detail-value">{product.ingredients.substring(0, 30)}...</span>
                            </div>
                          )}
                          {product.usage_instructions && (
                            <div className="product-details">
                              <span className="detail-label">Usage:</span>
                              <span className="detail-value">{product.usage_instructions.substring(0, 30)}...</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td>{getProductCategories(product)}</td>
                      <td>{getSubcategoryName(product.subcategory_id)}</td>
                      <td>{getBrandName(product.brand_id)}</td>
                      <td>
                        <span className="weight-info">
                          {product.weight || "N/A"}
                        </span>
                      </td>
                      <td>
                        <div className="price-info">
                          <span className="regular-price">${product.regular_price}</span>
                          {Number(product.discount) > 0 && (
                            <span className="discount">-{product.discount}%</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="stock-info">
                          <span
                            className={`stock ${
                              isOutOfStock(product) ? "out-of-stock" : 
                              isLowStock(product) ? "low-stock" : "in-stock"
                            }`}
                          >
                            {getTotalQuantity(product)}
                          </span>
                          {isLowStock(product) && (
                            <div className="low-stock-indicator">
                              <span className="low-stock-badge">Low Stock</span>
                            </div>
                          )}
                          {hasVariants(product) && (
                            <div className="variant-info">
                              <small className="variant-count">
                                {product.variants.length} variant{product.variants.length !== 1 ? 's' : ''}
                              </small>
                              <div className="variant-quantities">
                                {product.variants.map((variant, index) => (
                                  <small key={index} className={`variant-qty ${(variant.quantity || 0) <= 5 ? 'low-stock-variant' : ''}`}>
                                    {variant.color || variant.size || `V${index + 1}`}: {variant.quantity || 0}
                                  </small>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="tags-display">
                          {product.tags ? (
                            <div className="tags-list">
                              {product.tags.split(',').map((tag, index) => (
                                <span key={index} className="tag">
                                  {tag.trim()}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="no-tags">No tags</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="status-badges">
                          {product.is_trending && (
                            <span className="badge trending">Trending</span>
                          )}
                          {product.is_new && <span className="badge new">New</span>}
                          {product.hot_offer && <span className="badge hot-offer">Hot Offer</span>}
                        </div>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-edit"
                            onClick={() => handleEdit(product)}
                            title="Edit"
                          >
                            <svg viewBox="0 0 24 24">
                              <path
                                d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                              <path
                                d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                            </svg>
                          </button>
                          <button
                            className="btn-variants"
                            onClick={() => handleViewVariants(product)}
                            title="View Variants"
                          >
                            <svg viewBox="0 0 24 24">
                              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="currentColor"/>
                              <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => handleDelete(product.id)}
                            title="Delete"
                          >
                            <svg viewBox="0 0 24 24">
                              <path
                                d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                              <line
                                x1="10"
                                y1="11"
                                x2="10"
                                y2="17"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                              <line
                                x1="14"
                                y1="11"
                                x2="14"
                                y2="17"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total} products
          </div>
          {pagination.last_page > 1 && (
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
              >
                Previous
              </button>
              
              {Array.from({ length: pagination.last_page }, (_, i) => i + 1)
                .filter(page => {
                  const current = pagination.current_page;
                  return page === 1 || page === pagination.last_page || 
                         (page >= current - 2 && page <= current + 2);
                })
                .map((page, index, array) => {
                  const showEllipsis = index > 0 && page - array[index - 1] > 1;
                  return (
                    <React.Fragment key={page}>
                      {showEllipsis && <span className="pagination-ellipsis">...</span>}
                      <button
                        className={`pagination-btn ${page === pagination.current_page ? 'active' : ''}`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                })}
              
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.last_page}
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingProduct ? "Edit Product" : "Add New Product"}</h2>
                <button className="modal-close" onClick={closeModal}>
                  <svg viewBox="0 0 24 24">
                    <path
                      d="M18 6L6 18M6 6l12 12"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </button>
              </div>

              <div className="modal-body">
                <form onSubmit={handleSubmit} className="modal-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Product Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter product name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="sku">SKU</label>
                    <input
                      type="text"
                      id="sku"
                      name="sku"
                      value={formData.sku}
                      onChange={handleInputChange}
                      placeholder="Enter SKU (optional)"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="category_id">Primary Category</label>
                    <select
                      id="category_id"
                      name="category_id"
                      value={formData.category_id}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Primary Category</option>
                      {Array.isArray(categories) &&
                        categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Additional Categories (Optional)</label>
                    <div className="category-checkboxes">
                      {Array.isArray(categories) &&
                        categories.map((category) => (
                          <label key={category.id} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={formData.category_ids?.includes(category.id) || false}
                              onChange={() => handleCategoryChange(category.id)}
                            />
                            <span>{category.name}</span>
                          </label>
                        ))}
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="subcategory_id">Subcategory (Optional)</label>
                  <select
                    id="subcategory_id"
                    name="subcategory_id"
                    value={formData.subcategory_id}
                    onChange={handleInputChange}
                    disabled={subcategoriesLoading}
                  >
                    <option value="">{subcategoriesLoading ? "Loading..." : "Select Subcategory"}</option>
                    {Array.isArray(subcategories) &&
                      // If API returned all subcategories previously, keep a safe filter with type coercion
                      subcategories
                        .filter(sub => !formData.category_id || Number(sub.category_id) === Number(formData.category_id))
                        .map((subcategory) => (
                          <option key={subcategory.id} value={subcategory.id}>
                            {subcategory.name}
                          </option>
                        ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="desc">Description</label>
                  <textarea
                    id="desc"
                    name="desc"
                    value={formData.desc}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Enter product description"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="weight">Weight (Optional)</label>
                  <input
                    type="text"
                    id="weight"
                    name="weight"
                    value={formData.weight}
                    onChange={handleInputChange}
                    placeholder="e.g., 100ml, 50g, 1kg"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="ingredients">Ingredients (Optional)</label>
                  <textarea
                    id="ingredients"
                    name="ingredients"
                    value={formData.ingredients}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="List the main ingredients"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="usage_instructions">How to Use (Optional)</label>
                  <textarea
                    id="usage_instructions"
                    name="usage_instructions"
                    value={formData.usage_instructions}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Instructions on how to use this product"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="tags">Tags (Optional)</label>
                  <input
                    type="text"
                    id="tags"
                    name="tags"
                    value={formData.tags}
                    onChange={handleInputChange}
                    placeholder="e.g., organic, vegan, premium, limited edition"
                  />
                  <small className="form-help">
                    Separate multiple tags with commas (e.g., organic, vegan, premium)
                  </small>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="brand_id">Brand</label>
                    <select
                      id="brand_id"
                      name="brand_id"
                      value={formData.brand_id}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Brand</option>
                      {Array.isArray(brands) &&
                        brands.map((brand) => (
                          <option key={brand.id} value={brand.id}>
                            {brand.name}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="quantity">Quantity (Optional)</label>
                    <input
                      type="number"
                      id="quantity"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleInputChange}
                      min="0"
                      placeholder="0"
                    />
                    {hasNewVariantData() && (
                      <small className="form-help">
                        Quantity will be calculated from variant quantities
                      </small>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="buying_price">Buying Price (Optional)</label>
                    <input
                      type="number"
                      id="buying_price"
                      name="buying_price"
                      value={formData.buying_price}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="regular_price">Regular Price (Optional)</label>
                    <input
                      type="number"
                      id="regular_price"
                      name="regular_price"
                      value={formData.regular_price}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="discount">Discount (%)</label>
                    <input
                      type="number"
                      id="discount"
                      name="discount"
                      value={formData.discount}
                      onChange={handleInputChange}
                      min="0"
                      max="100"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="form-group">
  <label htmlFor="image">Product Image</label>
  <input
    type="file"
    id="image"
    name="image"
    onChange={handleInputChange}
    accept="image/*"
    // Remove 'multiple' - only allow single image
  />
  {editingProduct && editingProduct.image && (
    <div className="current-image">
      <p>Current image:</p>
      <img 
        src={editingProduct.image_url || `${API_BASE.replace("/api", "")}/storage/${editingProduct.image}`} 
        alt="Current product" 
        style={{maxWidth: '200px', maxHeight: '200px'}}
      />
    </div>
  )}
</div>

                

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="new_until">New Until</label>
                    <input
                      type="date"
                      id="new_until"
                      name="new_until"
                      value={formData.new_until}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-checkboxes">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_trending"
                      checked={formData.is_trending}
                      onChange={handleInputChange}
                    />
                    <span>Trending Product</span>
                  </label>

                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_new"
                      checked={formData.is_new}
                      onChange={handleInputChange}
                    />
                    <span>New Product</span>
                  </label>

                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="hot_offer"
                      checked={formData.hot_offer}
                      onChange={handleInputChange}
                    />
                    <span>Hot Offer</span>
                  </label>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader size={16} strokeWidth={2} />
                        {editingProduct ? "Updating..." : "Creating..."}
                      </>
                    ) : (
                      editingProduct ? "Update Product" : "Create Product"
                    )}
                  </button>
                </div>
              </form>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={cancelDelete}
          onConfirm={confirmDelete}
          title="Delete Product"
          message="Are you sure you want to delete this product?"
          itemName={deletingProduct ? deletingProduct.name : ""}
          isLoading={isDeleting}
        />

        {/* Product Variants Modal */}
        {showVariantsModal && (
          <div className="modal-overlay" onClick={closeVariantsModal}>
            <div className="modal-content variants-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Product Variants - {selectedProductForVariants?.name}</h2>
                <button className="modal-close" onClick={closeVariantsModal}>
                  <svg viewBox="0 0 24 24">
                    <path
                      d="M18 6L6 18M6 6l12 12"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </button>
              </div>

              <div className="modal-body">
                <div className="variants-header">
                  <h3>Variants ({productVariants.length})</h3>
                  <button 
                    className="btn-primary"
                    onClick={() => setShowAddVariantModal(true)}
                  >
                    Add New Variant
                  </button>
                </div>

                {variantsLoading ? (
                  <div className="loading">Loading variants...</div>
                ) : (
                  <div className="variants-list">
                    {productVariants.length === 0 ? (
                      <div className="empty-state">
                        <p>No variants found for this product.</p>
                        <button 
                          className="btn-primary"
                          onClick={() => setShowAddVariantModal(true)}
                        >
                          Add First Variant
                        </button>
                      </div>
                    ) : (
                      productVariants.map((variant, index) => (
                        <div key={variant.id} className="variant-card">
                          <div className="variant-header">
                            <div className="variant-info">
                              <div className="variant-order-info">
                                <span className="variant-order">#{variant.sort_order || index + 1}</span>
                                <h4>
                                  {variant.color && variant.size 
                                    ? `${variant.color} - ${variant.size}`
                                    : variant.color || variant.size || 'No Color/Size'
                                  }
                                </h4>
                              </div>
                              <div className="variant-details">
                                {variant.color && (
                                  <div className="color-display">
                                    <div 
                                      className="color-swatch"
                                      style={{ backgroundColor: variant.hex_color || '#CCCCCC' }}
                                      title={variant.hex_color}
                                    />
                                    <span className="hex-code">{variant.hex_color}</span>
                                  </div>
                                )}
                                {variant.size && (
                                  <div className="size-display">
                                    <span className="label">Size:</span>
                                    <span className="value">{variant.size}</span>
                                  </div>
                                )}
                                {variant.sku && (
                                  <div className="variant-sku">
                                    <span className="label">SKU:</span>
                                    <span className="value">{variant.sku}</span>
                                  </div>
                                )}
                                <div className="variant-quantity">
                                  <span className="label">Stock:</span>
                                  <span className={`value ${(variant.quantity || 0) > 0 ? 'in-stock' : 'out-of-stock'}`}>
                                    {variant.quantity || 0}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="variant-actions">
                              <div className="variant-order-controls">
                                <button 
                                  className="btn-order btn-sm"
                                  onClick={() => handleMoveVariantUp(variant.id)}
                                  disabled={index === 0 || variantsLoading}
                                  title="Move up"
                                >
                                  ↑
                                </button>
                                <button 
                                  className="btn-order btn-sm"
                                  onClick={() => handleMoveVariantDown(variant.id)}
                                  disabled={index === productVariants.length - 1 || variantsLoading}
                                  title="Move down"
                                >
                                  ↓
                                </button>
                              </div>
                              <button 
                                className="btn-edit btn-sm"
                                onClick={() => handleEditVariant(variant)}
                                title="Edit variant"
                              >
                                Edit
                              </button>
                            <button 
                              className="btn-delete btn-sm"
                              onClick={() => handleDeleteVariant(variant.id)}
                                title="Delete variant"
                            >
                                Delete
                            </button>
                            </div>
                          </div>
                          
                          <div className="variant-images">
                            {variant.images?.map((image) => (
                              <div key={image.id} className="image-container">
                                <img 
                                  src={image.url} 
                                  alt={`${selectedProductForVariants?.name} - ${variant.color || variant.size || 'Variant'}`}
                                  className="variant-image"
                                />
                                <button 
                                  className="delete-image-btn"
                                  onClick={() => handleDeleteImage(variant.id, image.id)}
                                  title="Delete image"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Add Variant Modal */}
        {showAddVariantModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3>Add New Variant</h3>
                <button 
                  className="modal-close"
                  onClick={() => setShowAddVariantModal(false)}
                >
                  <svg viewBox="0 0 24 24">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </button>
              </div>
              
              <div className="modal-body">
                <div className="form-group">
                  <label>Color Name (Required):</label>
                  <input
                    type="text"
                    value={newVariant.color}
                    onChange={(e) => setNewVariant(prev => ({ ...prev, color: e.target.value }))}
                    placeholder="e.g., Cherry Red, Navy Blue"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Hex Color Code (Optional):</label>
                  <div className="color-input-group">
                    <input
                      type="text"
                      value={newVariant.hex_color}
                      onChange={(e) => setNewVariant(prev => ({ ...prev, hex_color: e.target.value }))}
                      placeholder="#FF69B4"
                      pattern="^#[0-9A-Fa-f]{6}$"
                    />
                    <input
                      type="color"
                      value={newVariant.hex_color || '#CCCCCC'}
                      onChange={(e) => setNewVariant(prev => ({ ...prev, hex_color: e.target.value }))}
                      className="color-picker"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Size (Optional):</label>
                  <input
                    type="text"
                    value={newVariant.size}
                    onChange={(e) => setNewVariant(prev => ({ ...prev, size: e.target.value }))}
                    placeholder="e.g., S, M, L, XL, 36, 37, 38"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>SKU (Optional):</label>
                    <input
                      type="text"
                      value={newVariant.sku}
                      onChange={(e) => setNewVariant(prev => ({ ...prev, sku: e.target.value }))}
                      placeholder="e.g., TSHIRT-RED-M"
                      maxLength="100"
                    />
                  </div>

                  <div className="form-group">
                    <label>Quantity (Optional):</label>
                    <input
                      type="number"
                      value={newVariant.quantity}
                      onChange={(e) => setNewVariant(prev => ({ ...prev, quantity: e.target.value }))}
                      placeholder="0"
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Regular Price (Optional):</label>
                    <input
                      type="number"
                      value={newVariant.regular_price || ''}
                      onChange={(e) => setNewVariant(prev => ({ ...prev, regular_price: e.target.value }))}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                    <small className="form-help">Leave empty to use product price</small>
                  </div>

                  <div className="form-group">
                    <label>Discount % (Optional):</label>
                    <input
                      type="number"
                      value={newVariant.discount || ''}
                      onChange={(e) => setNewVariant(prev => ({ ...prev, discount: e.target.value }))}
                      placeholder="0"
                      min="0"
                      max="100"
                    />
                    <small className="form-help">Leave empty to use product discount</small>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Buying Price (Optional):</label>
                    <input
                      type="number"
                      value={newVariant.buying_price || ''}
                      onChange={(e) => setNewVariant(prev => ({ ...prev, buying_price: e.target.value }))}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                    <small className="form-help">Leave empty to use product buying price</small>
                  </div>

                  <div className="form-group">
                    <label>Weight (Optional):</label>
                    <input
                      type="text"
                      value={newVariant.weight || ''}
                      onChange={(e) => setNewVariant(prev => ({ ...prev, weight: e.target.value }))}
                      placeholder="e.g., 100ml, 50g"
                    />
                    <small className="form-help">Leave empty to use product weight</small>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Images:</label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                  {newVariant.images.length > 0 && (
                    <div className="image-previews">
                      {newVariant.images.map((image, index) => (
                        <div key={index} className="image-preview">
                          <img src={URL.createObjectURL(image)} alt={`Preview ${index + 1}`} />
                          <span>{image.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="modal-footer">
                <button 
                  className="btn-secondary"
                  onClick={() => setShowAddVariantModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn-primary"
                  onClick={handleAddVariant}
                  disabled={variantsLoading}
                >
                  {variantsLoading ? 'Adding...' : 'Add Variant'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Variant Modal */}
        {showEditVariantModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3>Edit Variant</h3>
                <button 
                  className="modal-close"
                  onClick={() => setShowEditVariantModal(false)}
                >
                  <svg viewBox="0 0 24 24">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </button>
              </div>
              
              <div className="modal-body">
                <div className="form-group">
                  <label>Color Name (Required):</label>
                  <input
                    type="text"
                    value={editVariantForm.color}
                    onChange={(e) => setEditVariantForm(prev => ({ ...prev, color: e.target.value }))}
                    placeholder="e.g., Cherry Red, Navy Blue"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Hex Color Code (Optional):</label>
                  <div className="color-input-group">
                    <input
                      type="text"
                      value={editVariantForm.hex_color}
                      onChange={(e) => setEditVariantForm(prev => ({ ...prev, hex_color: e.target.value }))}
                      placeholder="#FF69B4"
                      pattern="^#[0-9A-Fa-f]{6}$"
                    />
                    <input
                      type="color"
                      value={editVariantForm.hex_color || '#CCCCCC'}
                      onChange={(e) => setEditVariantForm(prev => ({ ...prev, hex_color: e.target.value }))}
                      className="color-picker"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Size (Optional):</label>
                  <input
                    type="text"
                    value={editVariantForm.size}
                    onChange={(e) => setEditVariantForm(prev => ({ ...prev, size: e.target.value }))}
                    placeholder="e.g., S, M, L, XL, 36, 37, 38"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>SKU (Optional):</label>
                    <input
                      type="text"
                      value={editVariantForm.sku}
                      onChange={(e) => setEditVariantForm(prev => ({ ...prev, sku: e.target.value }))}
                      placeholder="e.g., TSHIRT-RED-M"
                      maxLength="100"
                    />
                  </div>

                  <div className="form-group">
                    <label>Quantity (Optional):</label>
                    <input
                      type="number"
                      value={editVariantForm.quantity}
                      onChange={(e) => setEditVariantForm(prev => ({ ...prev, quantity: e.target.value }))}
                      placeholder="0"
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Regular Price (Optional):</label>
                    <input
                      type="number"
                      value={editVariantForm.regular_price || ''}
                      onChange={(e) => setEditVariantForm(prev => ({ ...prev, regular_price: e.target.value }))}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                    <small className="form-help">Leave empty to use product price</small>
                  </div>

                  <div className="form-group">
                    <label>Discount % (Optional):</label>
                    <input
                      type="number"
                      value={editVariantForm.discount || ''}
                      onChange={(e) => setEditVariantForm(prev => ({ ...prev, discount: e.target.value }))}
                      placeholder="0"
                      min="0"
                      max="100"
                    />
                    <small className="form-help">Leave empty to use product discount</small>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Buying Price (Optional):</label>
                    <input
                      type="number"
                      value={editVariantForm.buying_price || ''}
                      onChange={(e) => setEditVariantForm(prev => ({ ...prev, buying_price: e.target.value }))}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                    <small className="form-help">Leave empty to use product buying price</small>
                  </div>

                  <div className="form-group">
                    <label>Weight (Optional):</label>
                    <input
                      type="text"
                      value={editVariantForm.weight || ''}
                      onChange={(e) => setEditVariantForm(prev => ({ ...prev, weight: e.target.value }))}
                      placeholder="e.g., 100ml, 50g"
                    />
                    <small className="form-help">Leave empty to use product weight</small>
                  </div>
                </div>

                <div className="form-group">
                  <label>Sort Order:</label>
                  <input
                    type="number"
                    value={editVariantForm.sort_order}
                    onChange={(e) => setEditVariantForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                    min="0"
                    placeholder="0"
                  />
                  <small>Lower numbers appear first</small>
                </div>
                
                <div className="form-group">
                  <label>Add New Images (optional):</label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleEditVariantImageUpload}
                  />
                  {editVariantForm.images.length > 0 && (
                    <div className="image-previews">
                      {editVariantForm.images.map((image, index) => (
                        <div key={index} className="image-preview">
                          <img src={URL.createObjectURL(image)} alt={`Preview ${index + 1}`} />
                          <span>{image.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {editingVariant && editingVariant.images && editingVariant.images.length > 0 && (
                  <div className="form-group">
                    <label>Current Images:</label>
                    <div className="current-images">
                      {editingVariant.images.map((image) => (
                        <div key={image.id} className="current-image">
                          <img src={image.url} alt={`${editingVariant.color} variant`} />
                          <button 
                            className="delete-image-btn"
                            onClick={() => handleDeleteImage(editingVariant.id, image.id)}
                            title="Delete image"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="modal-footer">
                <button 
                  className="btn-secondary"
                  onClick={() => setShowEditVariantModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn-primary"
                  onClick={handleUpdateVariant}
                  disabled={isUpdatingVariant}
                >
                  {isUpdatingVariant ? 'Updating...' : 'Update Variant'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Variant Confirmation Modal */}
        {showDeleteVariantModal && (
          <div className="modal-overlay" onClick={cancelDeleteVariant}>
            <div className="modal-content confirmation-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Delete Variant</h3>
                <button className="modal-close" onClick={cancelDeleteVariant}>
                  <svg viewBox="0 0 24 24">
                    <path
                      d="M18 6L6 18M6 6l12 12"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </button>
              </div>
              
              <div className="modal-body">
                <p>Are you sure you want to delete this variant?</p>
                {variantToDelete && (
                  <div className="variant-name">
                    "{variantToDelete.color}"
                  </div>
                )}
                <div className="warning-banner">
                  <span className="warning-icon">⚠️</span>
                  <span className="warning-text">This action cannot be undone.</span>
                </div>
              </div>
              
              <div className="modal-footer">
                <button 
                  className="btn-secondary"
                  onClick={cancelDeleteVariant}
                  disabled={variantsLoading}
                >
                  Cancel
                </button>
                <button 
                  className="btn-primary btn-danger"
                  onClick={confirmDeleteVariant}
                  disabled={variantsLoading}
                >
                  {variantsLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
